import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { ConfirmationService } from 'primeng/api';
import { deleteTopic, setTopics } from 'app/redux/actions';
import { TopicResponse } from 'app/models';
import { Content } from 'app/constants';
import { SidebarList } from 'app/models';
import { TopicService } from 'app/services';
import { ToastNotificationService } from 'app/components';

export interface TopicLabelFilter {
  label: string;
  exclude?: boolean;
}

@Component({
  selector: 'app-topics-list',
  template: `
    <app-sidebar-entity-table
      kind="topic"
      [items]="topics"
      [allItems]="sectionTopics"
      [showCompetitorAction]="!!labelFilter?.exclude"
      (create)="handleCreateNewTopic()"
      (selected)="selectedTopic($event)"
      (edit)="handleEditTopic($event)"
    ></app-sidebar-entity-table>
  `,
})
export class SidebarTopicsListComponent implements OnInit {
  @Input() labelFilter: TopicLabelFilter;
  @Output() createNewTopicOnclick: EventEmitter<any> = new EventEmitter();
  @Output() editTopicOnclick: EventEmitter<any> = new EventEmitter();
  @Output() selectedTopicOnClick: EventEmitter<any> = new EventEmitter();
  public selectTopic: any;
  public topics: SidebarList[];
  public sectionTopics: SidebarList[];
  private allTopics: SidebarList[];
  public sort: any;
  public showScrollbar: boolean;
  public isEdit: boolean;
  public isUnfollow: boolean;
  public currentProject: any;
  public userId: any;
  public parentName: string;
  public reordering: boolean;

  @ViewChildren('sortNameIcon, sortCategoryIcon', {
    read: ElementRef,
  })
  sortIcons: QueryList<ElementRef>;

  constructor(
    private store: Store<AppState>,
    private confirmationService: ConfirmationService,
    private topicService: TopicService,
    private notificationService: ToastNotificationService
  ) {
    this.topics = [];
    this.sectionTopics = [];
    this.allTopics = [];
    this.showScrollbar = false;
    this.isEdit = false;
    this.isUnfollow = false;
    this.userId = null;
    this.parentName = Content.Topic;
    this.reordering = false;
  }

  ngOnInit() {
    this.store.select('userData').subscribe(({ data }) => {
      if (data) {
        this.userId = data.userId;
      }
    });

    this.store.select('topics').subscribe(({ topics }) => {
      if (topics && topics.length > 0) {
        this.allTopics = topics.map(
          (item) =>
            new SidebarList({
              title: item.name,
              subTitle: item?.categoryName,
              loadingIcon: false,
              picture: null,
              hasPicture: false,
              parentObject: item,
              parentName: this.parentName,
            })
        );
        this.sectionTopics = this.allTopics.filter((item) =>
          this.matchesLabelFilter(item.parentObject)
        );
        const visibleTopics = this.sectionTopics.filter((item) =>
          this.isVisibleTopic(item.parentObject)
        );
        this.topics = [
          ...visibleTopics.filter((item) => !!item.subTitle),
          ...visibleTopics.filter((item) => !item.subTitle),
        ];
      } else {
        this.allTopics = [];
        this.sectionTopics = [];
        this.topics = [];
      }
      this.showScrollbar = this.topics.length > 10;
    });

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.currentProject = projectSelected;
      }
    });

    this.store
      .select('topicDialog')
      .subscribe(({ topicSelected, loading, loaded, isSidebarTopic }) => {
        if (isSidebarTopic) {
          if (topicSelected) {
            this.loadingTopicDialog(topicSelected.userTopicId, loading, loaded);
          }
        }
      });
  }

  public handleCreateNewTopic() {
    this.createNewTopicOnclick.emit(null);
  }

  public handleEditTopic(index) {
    this.editTopicOnclick.emit(index);
  }

  public selectedTopic(topic) {
    this.selectedTopicOnClick.emit(topic);
  }

  public reorderTopics(reordered: SidebarList[]) {
    const previous = this.topics;
    this.topics = reordered;
    this.reordering = true;

    const reorderedItems = [...reordered];
    const completeOrder = this.allTopics.map((item) =>
      this.isVisibleTopic(item.parentObject) ? reorderedItems.shift() : item
    );
    const orderedIds = completeOrder.map(
      (item) => item.parentObject.userTopicId
    );
    this.topicService
      .updateTopicOrder(this.currentProject.value, orderedIds)
      .subscribe(
        () => {
          this.reordering = false;
          this.store.dispatch(
            setTopics({ projectId: this.currentProject.value })
          );
        },
        () => {
          this.topics = previous;
          this.reordering = false;
          this.notificationService.showMessage(
            'Unable to save topic order.',
            'Error'
          );
        }
      );
  }

  private matchesLabelFilter(topic: any): boolean {
    if (!this.labelFilter) {
      return true;
    }
    const matches = topic?.categoryName === this.labelFilter.label;
    return this.labelFilter.exclude ? !matches : matches;
  }

  private isVisibleTopic(topic: any): boolean {
    return topic?.categoryName !== 'Ignore' && this.matchesLabelFilter(topic);
  }

  loadingTopicDialog(topicId, loading, loaded) {
    const index = this.topics.findIndex(
      (topic) => topic.parentObject.userTopicId === topicId
    );
    if (index < 0) {
      return;
    }
    if (loading || loaded) {
      this.topics = this.topics.map((topic, topicIndex) =>
        topicIndex === index ? { ...topic, loadingIcon: loading } : topic
      );
    }
  }

  handleUnfollow(value) {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to unfollow this topic?',
      header: 'Unfollow Confirmation',
      icon: 'pi pi-exclamation-triangle',
      key: 'unfollowConfirmation',
      accept: () => {
        this.unfollow(value);
      },
      reject: (type) => {
        // handle rejected option ConfirmEventType.CANCEL
      },
    });
  }

  public unfollow(value) {
    const payload = {
      id: value.id,
      name: value.name,
      topicId: value.userTopicId,
      userId: this.userId,
      userProject: this.currentProject,
    };
    this.store.dispatch(deleteTopic(payload));
  }
}
